import { useRef, useState } from 'react';
import Typography from '@mui/material/Typography';
import { Box, Button, Link, InputAdornment, IconButton, TextField, Container } from '@mui/material';
import { useStyles } from '../../assets/LoginStyle.jsx';
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { VBox, HBox } from '../../assets/SharedComponents';
import { useTranslation } from 'react-i18next';
import { CustomBusy } from '../Common/CustomBusy.jsx';
import useAuth0Feathers from 'hooks/useAuth0Feathers.js';
import { useSnackbar } from 'notistack';
import { MessageBox } from '../Common/MessageBox';
import InfoIcon from '@mui/icons-material/Info';
import { feathersClient } from 'api/client';

export const IdPassTab = ({ setLoginIdProcess }) => {
    const [showPassword, setShowPassword] = useState(false);
    const classes = useStyles();
    const { t } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();
    const [id, setId] = useState("");
    const [password, setPass] = useState("");
    const hookFeathers = useAuth0Feathers();
    const [busy, setBusy] = useState(false);
    const [messageBoxState, setMessageBox] = useState({ open: false });
    const inputRef = useRef();

    const loginIdPassHandler = async (e) => {
        setBusy(true);
        try {
            console.log('🔐 Starting username/password login...');
            console.log('   Username:', id);

            // Just try to login - backend handles everything!
            await hookFeathers.loginWithUsernamePassword(id, password);

            console.log('✅ Login successful!');

        } catch (error) {
            console.error('❌ Login error:', error);

            if (error?.message === 'PASSWORD_RESET_SENT') {
                // Show password reset sent message
                setMessageBox({
                    open: true,
                    fullWidth: true,
                    maxWidth: "sm",
                    title: (
                        <Box display="flex" alignItems="center">
                            <InfoIcon sx={{ color: '#3a7735', fontSize: '2rem', ml: 1 }} />
                            <Typography variant="h5" paddingLeft="1rem" sx={{ color: '#3a7735' }}>
                                {t("passwordResetRequired") || "נדרש איפוס סיסמה"}
                            </Typography>
                        </Box>
                    ),
                    content: (
                        <Box textAlign="center" padding="2rem">
                            <Typography sx={{ marginBottom: "1.5rem", fontSize: "1.1rem" }}>
                                {t("passwordResetMessage1") || "המערכת עברה שדרוג אבטחה"}
                            </Typography>
                            <Typography sx={{ marginBottom: "1.5rem", fontSize: "1.1rem" }}>
                                {t("passwordResetMessage2") || "נשלח אליך מייל עם קישור לאיפוס סיסמה"}
                            </Typography>
                            <Box
                                sx={{
                                    backgroundColor: '#fff3cd',
                                    border: '1px solid #ffc107',
                                    borderRadius: '8px',
                                    padding: '1rem',
                                    marginTop: '1.5rem'
                                }}
                            >
                                <Typography sx={{ fontWeight: "bold", fontSize: "1.1rem" }}>
                                    {t("checkYourEmail") || "נא לבדוק את תיבת הדואר שלך:"}
                                </Typography>
                                <Typography sx={{ fontSize: "1.1rem", marginTop: "0.5rem", color: '#3a7735' }}>
                                    {error.email}
                                </Typography>
                            </Box>
                            <Typography sx={{ marginTop: "1.5rem", fontSize: "0.9rem", color: "#666" }}>
                                {t("resetPasswordAndRetry") || "לאחר איפוס הסיסמה, נסה להתחבר שוב"}
                            </Typography>
                        </Box>
                    ),
                    actions: (
                        <Button
                            className={classes.buttonGreen}
                            onClick={() => setMessageBox({ open: false })}
                        >
                            {t("understood") || "הבנתי"}
                        </Button>
                    )
                });
            } else if (error?.message === 'INVALID_CREDENTIALS') {
                enqueueSnackbar(t("UserNameOrPassError"), { variant: "error" });
            } else if (error?.message === 'ACCOUNT_BLOCKED') {
                enqueueSnackbar(t("accountBlocked") || "החשבון חסום, נא ליצור קשר עם התמיכה", { variant: "error" });
            } else {
                enqueueSnackbar(t("generalError") || "אירעה שגיאה, נסה שוב", { variant: "error" });
            }
        } finally {
            setBusy(false);
        }
    };

    const handleId = (e) => {
        let temp = e.target.value.replace(/\D/g, '').slice(0, 30);
        setId(temp);
    };

    const toggleShowPass = () => setShowPassword(curr => !curr);

    // UI logic
    const disabledButton = (!password) || (!id) || (id.length <= 7);

    return (
        <Box height={'100%'} className={classes.width100} display='inline-grid' flexDirection='column'>
            <Container>
                <VBox className={`${classes.paddingBottom1rem} ${classes.paddingTop2_5rem} `} width="17rem">
                    <TextField
                        type="text"
                        name="Id"
                        label={t("parentIdDoc")}
                        placeholder={t("idPlaceholder")}
                        style={{ width: "100% !important" }}
                        value={id}
                        className={`${classes.paddingBottom1rem} ${classes.RemoveTextFieldNumberArrow} ${classes.TextfieldILabelStyle}`}
                        onChange={e => handleId(e)}
                        variant="outlined"
                        onKeyDown={e => {
                            if (e.key === 'Enter' && !disabledButton) {
                                e.preventDefault();
                                loginIdPassHandler(e);
                            }
                        }}
                        InputProps={{
                            inputProps: {
                                inputMode: 'numeric',
                                pattern: "[0-9]*"
                            },
                            inputRef: inputRef
                        }}
                    />
                    <TextField
                        type={showPassword ? "text" : "password"}
                        name="Password"
                        value={password}
                        className={`${classes.paddingBottom1rem} ${classes.TextfieldILabelStyle}`}
                        onChange={e => setPass(e.target.value)}
                        style={{ width: "100% !important" }}
                        label={t("password")}
                        variant="outlined"
                        onKeyDown={e => {
                            if (e.key === 'Enter' && !disabledButton) {
                                e.preventDefault();
                                loginIdPassHandler(e);
                            }
                        }}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        aria-label="toggle password visibility"
                                        onClick={toggleShowPass}
                                        onMouseDown={toggleShowPass}
                                    >
                                        {showPassword ? <Visibility /> : <VisibilityOff />}
                                    </IconButton>
                                </InputAdornment>
                            )
                        }}
                    />
                    <HBox className={classes.spaceBetween}>
                        <Typography>
                            <Link
                                component="button"
                                color={'#007f00'}
                                className={`${classes.paddingInlineStart0_3} ${classes.LinkStyle}`}
                                onClick={e => { setLoginIdProcess(2) }}
                            >
                                {t("forgotPassword")}
                            </Link>
                        </Typography>
                    </HBox>

                    {busy ? (
                        <CustomBusy text={t("loginBusy")} />
                    ) : (
                        <Button
                            disabled={disabledButton}
                            sx={{ marginTop: "1rem" }}
                            className={`${disabledButton ? classes.buttonGreenLight : classes.buttonGreen}`}
                            variant="contained"
                            onClick={loginIdPassHandler}
                        >
                            {t("login")}
                        </Button>
                    )}
                </VBox>
            </Container>
            <MessageBox messageBoxState={messageBoxState} setMessageBox={setMessageBox} />
        </Box>
    );
};